import React, { useMemo, useState } from 'react';
import { Card, CardContent, Typography, Box, Chip, Stack, Avatar, TextField, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { employeesAPI, projectsAPI, invoicesAPI, clientsAPI } from '../services/api';
import { Employee } from '@/types';
import { useTechnologyLabels } from '../hooks/useTechnologies';
import { NAVIGATION } from '../constants';
import { formatNumber } from '../utils/formatNumber';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employeeFilter, setEmployeeFilter] = useState('');

  // Fetch data for summary cards
  const { data: employees = [], isLoading: employeesLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: employeesAPI.getAll
  });

  // Fetch technology labels
  const { data: technologyLabels = {}, isLoading: technologyLabelsLoading } = useTechnologyLabels();

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsAPI.getAll
  });

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: invoicesAPI.getAll
  });

  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsAPI.getAll
  });

  // Calculate analytics
  const activeEmployees = employees.filter((emp: any) => emp.isActive);
  
  const activeProjects = projects.filter((proj: any) => proj.isActive);

  // Employees currently not on any active project (consider current date between start/end)
  const unassignedEmployees = useMemo(() => {
    // Treat any project with isActive=true as valid for assignment list, ignore date bounds
    const assignedIds = new Set<string>();
    activeProjects.forEach((p: any) => {
      (p.employees || []).forEach((e: any) => assignedIds.add(e.id));
    });
    return activeEmployees
      .filter((e: any) => !assignedIds.has(e.id))
      .sort((a: Employee, b: Employee) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  }, [activeProjects, activeEmployees]);

  // Filter employees based on search term
  const filteredEmployees = useMemo(() => {
    if (!employeeFilter.trim()) return unassignedEmployees;
    
    const filterLower = employeeFilter.toLowerCase();
    return unassignedEmployees.filter((employee: any) => {
      // Filter by first name
      if (employee.firstName?.toLowerCase().includes(filterLower)) return true;
      
      // Filter by last name
      if (employee.lastName?.toLowerCase().includes(filterLower)) return true;
      
      // Filter by technologies
      if (employee.technologies && Array.isArray(employee.technologies)) {
        return employee.technologies.some((tech: string) => {
          const techLabel = technologyLabels[tech] || tech;
          return techLabel.toLowerCase().includes(filterLower);
        });
      }
      
      return false;
    });
  }, [unassignedEmployees, employeeFilter, technologyLabels]);

  const isLoading = employeesLoading || projectsLoading || invoicesLoading || clientsLoading || technologyLabelsLoading;

  return (
    <Box sx={{ p: 3, height: '100vh', overflow: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: 'text.primary' }}>
        Welcome back, {user?.employee?.firstName}!
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
        <Card 
          onClick={() => navigate(NAVIGATION.EMPLOYEES)}
          sx={{ 
            bgcolor: 'rgba(25, 118, 210, 0.1)', 
            border: '1px solid rgba(25, 118, 210, 0.2)',
            height: '100%',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(25, 118, 210, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
        >
          <CardContent>
            <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              Total Employees
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
              {isLoading ? '...' : activeEmployees.length}
            </Typography>
          </CardContent>
        </Card>

        <Card 
          onClick={() => navigate(NAVIGATION.CLIENTS)}
          sx={{ 
            bgcolor: 'rgba(245, 124, 0, 0.1)', 
            border: '1px solid rgba(245, 124, 0, 0.2)',
            height: '100%',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(245, 124, 0, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
        >
          <CardContent>
            <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              Total Clients
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 1 }}>
              {isLoading ? '...' : clients.filter((client: any) => client.isActive).length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
        <Card 
          onClick={() => navigate(NAVIGATION.PROJECTS)}
          sx={{ 
            bgcolor: 'rgba(156, 39, 176, 0.1)', 
            border: '1px solid rgba(156, 39, 176, 0.2)',
            height: '100%',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(156, 39, 176, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
        >
          <CardContent>
            <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              Total Projects
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 1 }}>
              {isLoading ? '...' : activeProjects.length}
            </Typography>
          </CardContent>
        </Card>

        <Card 
          onClick={() => navigate(NAVIGATION.INVOICES)}
          sx={{ 
            bgcolor: 'rgba(46, 125, 50, 0.1)', 
            border: '1px solid rgba(46, 125, 50, 0.2)',
            height: '100%',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(46, 125, 50, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
        >
          <CardContent>
            <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              Total Revenue
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 1 }}>
              {isLoading ? '...' : `€${formatNumber(invoices.reduce((sum: number, inv: any) => sum + (inv.totalAmount || 0), 0))}`}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Available employees section */}
      <Card sx={{ boxShadow: 6 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
              Available employees
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredEmployees.length} of {unassignedEmployees.length}
            </Typography>
          </Box>
          
          {/* Search filter */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or technology..."
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          {isLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : unassignedEmployees.length === 0 ? (
            <Typography color="text.secondary">Everyone is assigned to an active project.</Typography>
          ) : filteredEmployees.length === 0 ? (
            <Typography color="text.secondary">No employees match your search criteria.</Typography>
          ) : (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, 
              gap: 2 
            }}>
              {filteredEmployees.map((e: any) => (
                <Box 
                  key={e.id} 
                  onClick={() => navigate(`${NAVIGATION.EMPLOYEES}/${e.id}/edit`)}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    gap: 1.5, 
                    p: 2, 
                    border: '1px solid rgba(255, 255, 255, 0.12)', 
                    borderRadius: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    cursor: 'pointer',
                    textAlign: 'center',
                    minHeight: 160,
                    justifyContent: 'flex-start',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                      transition: 'background-color 0.2s ease-in-out'
                    }
                  }}
                >
                  <Avatar 
                    src={e.user?.profilePicture || e.profilePicture}
                    sx={{ bgcolor: '#1976d2', width: 48, height: 48 }}
                  >
                    {e.firstName.charAt(0)}{e.lastName.charAt(0)}
                  </Avatar>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {e.firstName} {e.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      {e.position}
                    </Typography>
                    {e.technologies && e.technologies.length > 0 ? (
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ justifyContent: 'center' }}>
                        {e.technologies.slice(0, 3).map((tech: string) => (
                          <Chip 
                            key={tech} 
                            label={technologyLabels[tech] || tech} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.65rem',
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                              color: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                bgcolor: 'rgba(255, 255, 255, 0.05)'
                              }
                            }}
                          />
                        ))}
                        {e.technologies.length > 3 && (
                          <Chip 
                            label={`+${e.technologies.length - 3}`}
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.65rem',
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                              color: 'rgba(255, 255, 255, 0.8)'
                            }}
                          />
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No technologies specified
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard; 